import { createContext, ReactNode, useEffect, useState } from "react";
import { setCookie, parseCookies, destroyCookie } from 'nookies'
import Router from 'next/router'

import { recoverUserInformation, signInRequest, verifyFellowOtp, verifyOtp } from "../services/authService";
import { api } from "../services/api";

import {AxiosResponse} from "axios";

type User = {
  id: string;
  name: string;
  perfil: string;
  nome: string;
  email: string;
  especialidade: string;
  situacao: string;
  foto: string;
  genero: string;
  celular_whatsapp: string;
  celular_2: string;
  telefone_fixo: string;
  endereco: string;
  dt_nascimento: string;
  mini_curriculum: string;
  curriculum_lates: string;
  maior_titulo_academico: string;
  document_number: string;
}

function isProfileComplete(user: User | null, role: string): boolean {
  if (!user) return false;

  if (role === 'boss') {
    return !!(
      user.nome &&
      user.email &&
      user.maior_titulo_academico &&
      user.curriculum_lates
    );
  }

  if (role === 'resident') {
    return !!(
      user.nome &&
      user.email &&
      user.document_number &&
      user.dt_nascimento &&
      user.curriculum_lates
    );
  }

  return false;
}

type Role = {
  role_name: string
}

type SignInData = {
  email: string;
  password: string;
  type: string;
}

type OtpSignInData = {
  challengeId: string;
  code: string;
}

interface AuthContextProviderProps {
  children: ReactNode;
}

type AuthContextType = {
  isAuthenticated: boolean;
  user: User | null;
  role: Role | null;
  signIn: (data: SignInData) => Promise<void>
  signInWithOtp: (data: OtpSignInData) => Promise<void>
  signInFellowWithOtp: (data: OtpSignInData) => Promise<void>
  signOut: () => void,
  updateUserInformations: (role:string) => void
}

export const AuthContext = createContext({} as AuthContextType)

export function AuthProvider({ children }: AuthContextProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [role, setRole] = useState<Role | null>(null)

  const isAuthenticated = !!user;

  async function establishSession(accessToken: string, type: string) {
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

    // Remove também cookies legados, que eram criados com o path atual (/auth).
    destroyCookie(undefined, 'nextauth.token');
    destroyCookie(undefined, 'user.role');
    destroyCookie(undefined, 'nextauth.token', {path: '/'});
    destroyCookie(undefined, 'user.role', {path: '/'});

    setCookie(undefined, 'nextauth.token', accessToken, {
      maxAge: 60 * 60,
      path: '/',
      sameSite: 'lax',
      secure: process.env.NODE_ENV === 'production',
    });
    setCookie(undefined, 'user.role', type, {
      maxAge: 60 * 60,
      path: '/',
      sameSite: 'lax',
      secure: process.env.NODE_ENV === 'production',
    });

    const responseUserInformation: AxiosResponse<any, any> = await recoverUserInformation(type);
    const userData = responseUserInformation.data.data as User;
    setUser(userData);
    setRole({role_name: type});

    if (!isProfileComplete(userData, type)) {
      await Router.push('/settings/personal-information');
    } else if (type === 'resident') {
      await Router.push('/rar');
    } else {
      await Router.push('/manager/program-informations');
    }
  }

  useEffect(() => {
    const { 'nextauth.token': token } = parseCookies()
    const { 'user.role': type } = parseCookies()

    if (token) {

      recoverUserInformation(type).then(response => {
        const userData = response.data.data as User;
        setUser(userData);
        setRole({role_name: type})

        if (!isProfileComplete(userData, type)) {
          Router.push('/settings/personal-information');
        }
      })

    }
  }, [])

  async function signIn({ email, password, type }: SignInData) {
    try{

      const responseSignRequest: AxiosResponse<any, any> = await signInRequest({email,password, type });
      const { access_token } = responseSignRequest.data;

      await establishSession(access_token, type);

    }catch (error: any){

      if(error.response)
        return Promise.reject(new Error(error.response.data.message));
      else
        return Promise.reject(new Error(error.message))

    }
  }


  async function signInWithOtp({challengeId, code}: OtpSignInData) {
    try {
      const response = await verifyOtp(challengeId, code);
      await establishSession(response.data.access_token, 'boss');
    } catch (error: any) {
      return Promise.reject(new Error(
        error.response?.data?.message || 'Código inválido ou expirado. Solicite um novo código.'
      ));
    }
  }

  async function signInFellowWithOtp({challengeId, code}: OtpSignInData) {
    try {
      const response = await verifyFellowOtp(challengeId, code);
      await establishSession(response.data.access_token, 'resident');
    } catch (error: any) {
      return Promise.reject(new Error(
        error.response?.data?.message || 'Código inválido ou expirado. Solicite um novo código.'
      ));
    }
  }

  async function updateUserInformations(role:string) {
    recoverUserInformation(role).then(response => {
      const userData = response.data.data as User;
      setUser(userData);

      if (isProfileComplete(userData, role)) {
        const currentPath = Router.pathname;
        if (currentPath === '/settings/personal-information') {
          if (role === 'resident') {
            Router.push('/rar');
          } else {
            Router.push('/manager/program-informations');
          }
        }
      }
    })
  }

  async function signOut() {

    destroyCookie(undefined, 'nextauth.token');
    destroyCookie(undefined, 'user.role')
    destroyCookie(undefined, 'nextauth.token', {path: '/'});
    destroyCookie(undefined, 'user.role', {path: '/'});

    Router.push('/login');
    
  }

  return (
    <AuthContext.Provider value={{ user, role, isAuthenticated, signIn, signInWithOtp, signInFellowWithOtp, signOut, updateUserInformations }}>
      {children}
    </AuthContext.Provider>
  )
}
